import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useExpert } from '../../contexts/ExpertContext';
import { useAuth } from '../../contexts/AuthContext';
import { BrButton } from '@govbr-ds/react-components';
import { LanguageToggle } from '../LanguageToggle';
import { useState } from 'react';

export default function ExpertLayout() {
    const { t } = useTranslation();
    const { getProgress, endSession } = useExpert();
    const { signOut } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const progress = getProgress();
    const [menuOpen, setMenuOpen] = useState(false);

    // Only show progress bar on evaluation pages
    const showProgress = location.pathname.includes('/avaliacao');

    const handleLogout = async () => {
        endSession();
        await signOut();
        navigate('/');
    };

    return (
        <div className="min-h-screen bg-gray-2 d-flex flex-column">
            {/* Header Gov.br - Compact */}
            <header className="br-header compact">
                <div className="container-lg">
                    <div className="header-top">
                        <div className="header-logo d-flex align-items-center">
                            <img
                                src="/logo-lumen.svg"
                                alt={t('expert.layout.title')}
                                className="logo-responsive"
                            />
                            <span className="br-divider vertical mx-2 d-none d-sm-block" style={{ height: 'auto', minHeight: '48px' }}></span>
                            <div className="d-flex flex-column">
                                <span className="text-lg text-weight-bold text-primary-default">
                                    {t('expert.layout.title')}
                                </span>
                                <span className="text-base text-gray-60">
                                    {t('expert.layout.subtitle')}
                                </span>
                            </div>
                        </div>

                        {/* Desktop: show actions inline */}
                        <div className="header-actions d-none d-md-flex align-items-center gap-3">
                            <LanguageToggle variant="primary" />
                            {showProgress && progress.total > 0 && (
                                <span className="text-lg text-primary-default text-weight-semi-bold">
                                    {progress.current}/{progress.total}
                                </span>
                            )}
                            <BrButton secondary onClick={handleLogout} aria-label={t('common.logout')}>
                                {t('common.logout')}
                            </BrButton>
                        </div>

                        {/* Mobile: hamburger button */}
                        <button
                            className="d-md-none br-button circle"
                            onClick={() => setMenuOpen(!menuOpen)}
                            aria-label="Menu"
                            aria-expanded={menuOpen}
                        >
                            <i className="fas fa-bars" aria-hidden="true"></i>
                        </button>
                    </div>

                    {/* Mobile menu dropdown */}
                    {menuOpen && (
                        <div className="d-md-none bg-pure-0 border-top py-3">
                            <div className="d-flex flex-column gap-3 px-3">
                                <div className="d-flex align-items-center justify-content-between">
                                    <span className="text-lg text-gray-80">{t('expert.layout.language')}</span>
                                    <LanguageToggle variant="primary" />
                                </div>
                                {showProgress && progress.total > 0 && (
                                    <div className="d-flex align-items-center justify-content-between">
                                        <span className="text-lg text-gray-80">{t('expert.layout.progress')}</span>
                                        <span className="text-lg text-primary-default text-weight-semi-bold">
                                            {progress.current}/{progress.total}
                                        </span>
                                    </div>
                                )}
                                <BrButton secondary onClick={handleLogout} className="w-100" aria-label={t('common.logout')}>
                                    {t('common.logout')}
                                </BrButton>
                            </div>
                        </div>
                    )}
                </div>
            </header>

            {/* Progress bar - only show during evaluation */}
            {showProgress && progress.total > 0 && (
                <div className="bg-pure-0 shadow-sm-down py-3">
                    <div className="container-lg">
                        <div className="d-flex align-items-center gap-3">
                            <div className="flex-fill">
                                <div className="bg-gray-10 rounded-pill overflow-hidden" style={{ height: '8px' }}>
                                    <div
                                        className="bg-success h-100"
                                        style={{ width: `${((progress.current - 1) / progress.total) * 100}%`, transition: 'width 0.3s' }}
                                    />
                                </div>
                            </div>
                            <span className="text-lg text-weight-bold text-primary-default">
                                {Math.round(((progress.current - 1) / progress.total) * 100)}%
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Main content */}
            <main className="flex-fill py-4">
                <div className="container-lg">
                    <Outlet />
                </div>
            </main>

            {/* Footer Gov.br */}
            <footer className="br-footer">
                <div className="container-lg">
                    <div className="row py-4 align-items-center">
                        <div className="col-12 col-md-6 text-center text-md-left mb-3 mb-md-0">
                            <p className="text-base mb-1">{t('expert.layout.researchBy')}</p>
                            <p className="text-lg text-weight-semi-bold">{t('expert.layout.university')}</p>
                        </div>
                        <div className="col-12 col-md-6 text-center text-md-right">
                            <p className="text-sm mb-1">{t('expert.layout.questions')}</p>
                            <a href="mailto:contact@lumenframework.com" className="text-pure-0 text-break text-xs">
                                contact@lumenframework.com
                            </a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
